import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

type Step = 'create' | 'verify' | 'done';

const Signup: React.FC = () => {
  const { signup, confirmSignup, resendConfirmation } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('create');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (step === 'done') {
      const timer = setTimeout(() => navigate('/login', { replace: true }), 1500);
      return () => clearTimeout(timer);
    }
  }, [step, navigate]);

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 0',
    fontSize: '16px',
    color: '#fff',
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid rgba(255,255,255,0.12)',
    outline: 'none',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signup(email, password);
      setStep('verify');
    } catch (err: any) {
      setError(err.message || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await confirmSignup(email, code);
      setStep('done');
    } catch (err: any) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    try {
      await resendConfirmation(email);
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        animation: 'fadeIn 300ms ease-in',
        '@keyframes fadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 400,
          bgcolor: '#0a0a0a',
          border: '1px solid rgba(255,255,255,0.12)',
          p: 4,
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <img src="/exo-logo.png" alt="Exo" style={{ height: 40, marginBottom: 12 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Canard
          </Typography>
        </Box>

        {step === 'create' && (
          <form onSubmit={handleCreate}>
            <Box sx={{ mb: 2.5 }}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                disabled={loading}
                required
                style={inputStyle}
              />
            </Box>
            <Box sx={{ mb: 1 }}>
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                required
                style={inputStyle}
              />
            </Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, mb: 3 }}>
              8 characters minimum
            </Typography>
            {error && (
              <Typography sx={{ color: '#ff6b6b', fontSize: 14, mb: 2 }}>{error}</Typography>
            )}
            <Button
              type="submit"
              fullWidth
              disabled={loading}
              sx={{
                bgcolor: '#EF8997',
                color: '#000',
                height: 48,
                fontWeight: 600,
                fontSize: 16,
                '&:hover': { bgcolor: '#e07080' },
                '&:disabled': { bgcolor: '#EF8997', opacity: 0.7 },
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: '#000' }} /> : 'Create account'}
            </Button>
            <Box sx={{ mt: 2.5, textAlign: 'center' }}>
              <Typography
                component="span"
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: 14,
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={() => navigate('/login')}
              >
                Already have an account? Sign in
              </Typography>
            </Box>
          </form>
        )}

        {step === 'verify' && (
          <form onSubmit={handleVerify}>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Check your email
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14, mb: 3 }}>
              We sent a verification code to {email}
            </Typography>
            <Box sx={{ mb: 3 }}>
              <input
                type="text"
                placeholder="Verification code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoFocus
                disabled={loading}
                required
                style={{
                  ...inputStyle,
                  fontFamily: 'monospace',
                  letterSpacing: '0.3em',
                  textAlign: 'center',
                  fontSize: '24px',
                }}
              />
            </Box>
            {error && (
              <Typography sx={{ color: '#ff6b6b', fontSize: 14, mb: 2 }}>{error}</Typography>
            )}
            <Button
              type="submit"
              fullWidth
              disabled={loading}
              sx={{
                bgcolor: '#EF8997',
                color: '#000',
                height: 48,
                fontWeight: 600,
                fontSize: 16,
                '&:hover': { bgcolor: '#e07080' },
                '&:disabled': { bgcolor: '#EF8997', opacity: 0.7 },
              }}
            >
              {loading ? <CircularProgress size={24} sx={{ color: '#000' }} /> : 'Verify'}
            </Button>
            <Box sx={{ mt: 2.5, textAlign: 'center' }}>
              <Typography
                component="span"
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  fontSize: 14,
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={handleResend}
              >
                Resend code
              </Typography>
            </Box>
          </form>
        )}

        {step === 'done' && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Account created
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>
              Redirecting to sign in...
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Signup;
